import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { type AddBillingMethodWorkflowVariant } from '#app/features/wallet/components/AddAccountWorkFlows/types.js';
import { useContraPayments } from '#app/hooks/useContraPayments.js';
import { useCreateBillingMethod } from '#app/hooks/useCreateBillingMethod.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useStripe } from '@stripe/react-stripe-js';
import { type StripeElements } from '@stripe/stripe-js';
import {
  createContext,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

export type BillingMethodEventOptions = {
  defaultData?: {
    email?: string;
  };
  hideSuccessModal?: boolean;
  onNewAchAccountRequiresApproval?: () => void;
  onNewAchAccountRequiresMicroDeposit?: () => void;
  onSuccess?: (args: { paymentMethod: string }) => void;
  successMessage?: string;
};

type AddBillingMethodContextProviderProps = {
  readonly children: ReactNode;
};

type AddBillingMethodWorkflowReactContextType = {
  initiateAddBillingMethodWorkflow: (
    workflowVariant?: AddBillingMethodWorkflowVariant,
  ) => void;
  isCreditCardFormSubmitting: boolean;
  isOrganizationWidePaymentMethod: boolean;
  newPaymentMethodAdded?: string;
  onCancelAddBillingMethodEvent: () => void;
  onStripeBillingMethodEvent: (
    data: StripeElements,
    options?: BillingMethodEventOptions,
    onError?: () => void,
  ) => void;
  setIsOrganizationWidePaymentMethod: (isPrivate: boolean) => void;
  setNewPaymentMethodAdded: (value?: string) => void;
  view: null | View;
  workflowActive: boolean;
  workflowVariant: AddBillingMethodWorkflowVariant;
};

type View = 'BILLING_METHOD_PENDING_VERIFICATION' | 'CREDIT_CARD_AUTHORING';

export const AddBillingMethodWorkflowReactContext =
  createContext<AddBillingMethodWorkflowReactContextType>({
    initiateAddBillingMethodWorkflow: () => {},
    isCreditCardFormSubmitting: false,
    isOrganizationWidePaymentMethod: true,
    newPaymentMethodAdded: undefined,
    onCancelAddBillingMethodEvent: () => {},
    onStripeBillingMethodEvent: () => {},
    setIsOrganizationWidePaymentMethod: () => {},
    setNewPaymentMethodAdded: () => {},
    view: null,
    workflowActive: false,
    workflowVariant: 'OTHER',
  });

export const AddBillingMethodContextProvider = ({
  children,
}: AddBillingMethodContextProviderProps) => {
  const stripe = useStripe();
  const toast = useToast();
  const location = useLocation();

  // these functions could be handled inline but they are
  // quite verbose so  been extracted to keep things easier to parse.
  const { refreshSetupIntentAndAttachPaymentMethodIfReady } =
    useCreateBillingMethod();

  const { userHasApprovedAchPayments } = useContraPayments();

  const [workflowVariant, setForkFlowVariant] =
    useState<AddBillingMethodWorkflowVariant>('OTHER');

  const [workflowActive, setWorkflowActive] = useState<boolean>(false);
  const [view, setView] = useState<null | View>(null);

  const [isCreditCardFormSubmitting, setIsCreditCardFormSubmitting] =
    useState<boolean>(false);

  const [isOrganizationWidePaymentMethod, setIsOrganizationWidePaymentMethod] =
    useState<boolean>(true);

  const initiateAddBillingMethodWorkflow = useCallback(
    (nextWorkflowVariant: AddBillingMethodWorkflowVariant = 'OTHER') => {
      setWorkflowActive(true);
      setForkFlowVariant(nextWorkflowVariant);
      setView('CREDIT_CARD_AUTHORING');
    },
    [],
  );

  const onCancelAddBillingMethodEvent = useCallback(() => {
    setIsCreditCardFormSubmitting(false);
    setView(null);
    setWorkflowActive(false);
  }, []);

  const [newPaymentMethodAdded, setNewPaymentMethodAdded] = useState<string>();
  const onStripeBillingMethodEvent = useCallback(
    (
      billingMethodElements: StripeElements,
      options?: BillingMethodEventOptions,
      onError?: () => void,
    ) => {
      if (!stripe) {
        return;
      }

      setIsCreditCardFormSubmitting(true);

      stripe
        .confirmSetup({
          confirmParams: {
            payment_method_data: {
              billing_details: options?.defaultData,
            },
            return_url: location.currentURL,
          },
          elements: billingMethodElements,
          redirect: 'if_required',
        })
        .then(async (setupIntentResponse) => {
          const setupIntent = setupIntentResponse.setupIntent;
          const paymentMethod = setupIntent?.payment_method;

          if (setupIntentResponse.setupIntent?.id) {
            await refreshSetupIntentAndAttachPaymentMethodIfReady({
              foreignStripeSetupIntentId: setupIntentResponse.setupIntent.id,
              visibilityToAttachAs: isOrganizationWidePaymentMethod
                ? 'WORKSPACE'
                : 'PRIVATE',
            });
          }

          const requiresMicroDeposit =
            setupIntent?.next_action?.type === 'verify_with_microdeposits';

          if (
            !setupIntent ||
            (setupIntent?.status !== 'succeeded' && !requiresMicroDeposit) ||
            !paymentMethod ||
            typeof paymentMethod !== 'string'
          ) {
            const stripeError = setupIntentResponse.error?.message;
            if (stripeError) {
              toast.error(
                `${stripeError} Contact support if you have questions.`,
              );
              if (onError) {
                onError();
              }

              return;
            }

            // Stripe can also return a `PaymentMethod`, and we are expecting only a string right now.
            // In the future we may need to properly handle a `PaymentMethod` return.
            throw new Error(
              'An error occurred creating your billing method on stripe',
            );
          } else if (
            setupIntent.payment_method_types.includes('us_bank_account') &&
            !userHasApprovedAchPayments
          ) {
            if (options?.onNewAchAccountRequiresApproval) {
              options?.onNewAchAccountRequiresApproval();
            }

            if (requiresMicroDeposit) {
              setView('BILLING_METHOD_PENDING_VERIFICATION');
              return;
            }
          } else if (requiresMicroDeposit) {
            setView('BILLING_METHOD_PENDING_VERIFICATION');

            if (options?.onNewAchAccountRequiresMicroDeposit) {
              options?.onNewAchAccountRequiresMicroDeposit();
            }

            return;
          }

          setNewPaymentMethodAdded(paymentMethod);

          if (!options?.hideSuccessModal) {
            setView(null);
          }

          let successMessage = 'Payment method added';
          if (options?.successMessage) {
            successMessage = options.successMessage;
          } else if (
            setupIntent.payment_method_types.includes('us_bank_account') &&
            !userHasApprovedAchPayments
          ) {
            successMessage =
              'Thank you for adding your bank details! Your request for ACH access has been sent.';
          }

          toast.success(successMessage);
          const onSuccess = options?.onSuccess;
          if (onSuccess) {
            onSuccess({ paymentMethod });
          }
        })
        .catch(() => {
          if (onError) {
            onError();
          }

          toast.error(
            'An error occurred attempting to save your new billing method.',
          );
        })
        .finally(() => {
          setIsCreditCardFormSubmitting(false);
        });
    },
    [
      stripe,
      location.currentURL,
      userHasApprovedAchPayments,
      toast,
      refreshSetupIntentAndAttachPaymentMethodIfReady,
      isOrganizationWidePaymentMethod,
    ],
  );

  const contextValue = useMemo(() => {
    return {
      initiateAddBillingMethodWorkflow,
      isCreditCardFormSubmitting,
      isOrganizationWidePaymentMethod,
      newPaymentMethodAdded,
      onCancelAddBillingMethodEvent,
      onStripeBillingMethodEvent,
      setIsOrganizationWidePaymentMethod,
      setNewPaymentMethodAdded,
      view,
      workflowActive,
      workflowVariant,
    };
  }, [
    initiateAddBillingMethodWorkflow,
    isCreditCardFormSubmitting,
    isOrganizationWidePaymentMethod,
    newPaymentMethodAdded,
    onCancelAddBillingMethodEvent,
    onStripeBillingMethodEvent,
    setNewPaymentMethodAdded,
    view,
    workflowActive,
    workflowVariant,
  ]);

  return (
    <AddBillingMethodWorkflowReactContext.Provider value={contextValue}>
      {children}
    </AddBillingMethodWorkflowReactContext.Provider>
  );
};
